import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from './ui/AnimatedSection';
import { SectionDivider } from './ui/SectionDivider';
import { SparkleDecoration } from './ui/SparkleDecoration';
const floatingParticles = [{
  x: '8%',
  y: '15%',
  size: 14,
  color: '#E8735A',
  delay: 0,
  duration: 6
}, {
  x: '85%',
  y: '20%',
  size: 10,
  color: '#F2A59D',
  delay: 1.2,
  duration: 7
}, {
  x: '15%',
  y: '75%',
  size: 12,
  color: '#6B1F2A',
  delay: 0.5,
  duration: 5.5
}, {
  x: '90%',
  y: '70%',
  size: 16,
  color: '#E8735A',
  delay: 2,
  duration: 8
}, {
  x: '50%',
  y: '10%',
  size: 8,
  color: '#F2A59D',
  delay: 0.8,
  duration: 6.5
}, {
  x: '70%',
  y: '85%',
  size: 11,
  color: '#E8735A',
  delay: 1.5,
  duration: 7.5
}, {
  x: '25%',
  y: '45%',
  size: 9,
  color: '#6B1F2A',
  delay: 3,
  duration: 6
}, {
  x: '60%',
  y: '55%',
  size: 13,
  color: '#F2A59D',
  delay: 0.3,
  duration: 5
}, {
  x: '40%',
  y: '90%',
  size: 10,
  color: '#E8735A',
  delay: 2.5,
  duration: 7
}, {
  x: '5%',
  y: '50%',
  size: 15,
  color: '#F2A59D',
  delay: 1,
  duration: 8.5
}];
function FloatingSparkle({
  x,
  y,
  size,
  color,
  delay,
  duration
}: (typeof floatingParticles)[0]) {
  return <motion.div className="absolute pointer-events-none z-0" style={{
    left: x,
    top: y
  }} animate={{
    y: [0, -18, 5, -12, 0],
    x: [0, 8, -6, 10, 0],
    rotate: [0, 90, 180, 270, 360],
    opacity: [0.3, 0.7, 0.4, 0.8, 0.3]
  }} transition={{
    duration,
    delay,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-271">
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color} data-id="element-272">
        <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41L12 0Z" data-id="element-273" />
      </svg>
    </motion.div>;
}
export function ProductIntroSection() {
  const mainCards = [{
    emoji: '📚',
    number: '6',
    label: 'Tomos Completos',
    accent: 'coral',
    border: 'border-coral/30',
    ring: 'bg-coral/15',
    shadowColor: 'rgba(232, 115, 90, 0.25)',
    edgeColor: '#E8735A'
  }, {
    emoji: '🎁',
    number: '6',
    label: 'Bonos Exclusivos',
    accent: 'burgundy',
    border: 'border-burgundy/20',
    ring: 'bg-burgundy/10',
    shadowColor: 'rgba(107, 31, 42, 0.2)',
    edgeColor: '#6B1F2A'
  }, {
    emoji: '📄',
    number: '+400',
    label: 'Páginas de contenido',
    accent: 'coral',
    border: 'border-coral/30',
    ring: 'bg-coral/15',
    shadowColor: 'rgba(232, 115, 90, 0.25)',
    edgeColor: '#E8735A'
  }];
  const bonusCard = {
    emoji: '🖨️',
    number: '+30',
    label: 'Fichas Imprimibles',
    accent: 'rose',
    border: 'border-rose/40',
    ring: 'bg-rose/25',
    shadowColor: 'rgba(242, 165, 157, 0.3)',
    edgeColor: '#F2A59D'
  };
  return <section id="el-pack" className="py-14 md:py-18 bg-rose/20 relative mt-10 overflow-hidden" data-id="element-274">
      <SectionDivider position="top" fillColor="fill-rose/20" data-id="element-275" />

      {/* Floating sparkle particles */}
      {floatingParticles.map((particle, i) => <FloatingSparkle key={i} {...particle} data-id="element-276" />)}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-277">
        <AnimatedSection className="text-center" data-id="element-278">
          {/* Badge — first element in the block */}
          <div className="flex justify-center mb-4" data-id="element-279">
            <span className="inline-block bg-coral text-white font-bold text-xs md:text-sm px-5 py-2 rounded-full shadow-md tracking-wide" data-id="element-280">
              ⭐ El sistema que miles de mamás ya están usando
            </span>
          </div>

          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-281">
            <SparkleDecoration size={20} color="#6B1F2A" data-id="element-282" />
            <h2 className="text-xl md:text-2xl font-serif font-bold text-charcoal" data-id="element-283">
              Presentamos el sistema que lo cambia todo
            </h2>
          </div>

          <h3 className="text-3xl md:text-4xl font-bold text-coral mb-4 leading-tight" data-id="element-284">
            Mega Pack Mamá Sin Estrés 2026
          </h3>

          <p className="text-base md:text-lg text-charcoal/80 mb-8 leading-relaxed max-w-xl mx-auto" data-id="element-285">
            El sistema completo para organizar tu familia, recuperar tu calma y
            disfrutar la maternidad de verdad. Sin fórmulas mágicas, con
            herramientas prácticas que se adaptan a tu vida real.
          </p>

          {/* Main 3 cards */}
          <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto" style={{
          perspective: '800px'
        }} data-id="element-286">
            {mainCards.map((item, i) => <div key={i} className={`relative bg-white ${item.border} border rounded-xl px-2.5 py-3 text-center transition-all duration-300 overflow-visible group cursor-pointer`} style={{
            transformStyle: 'preserve-3d',
            transform: 'rotateX(4deg) rotateY(0deg)',
            boxShadow: `0 4px 0 -1px ${item.edgeColor}40, 0 7px 0 -2px ${item.edgeColor}20, 0 10px 20px -6px ${item.shadowColor}, 0 2px 6px rgba(0,0,0,0.05)`
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg) translateY(-3px)';
            e.currentTarget.style.boxShadow = `0 7px 0 -1px ${item.edgeColor}40, 0 11px 0 -2px ${item.edgeColor}20, 0 16px 30px -6px ${item.shadowColor}, 0 3px 10px rgba(0,0,0,0.07)`;
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'rotateX(4deg) rotateY(0deg)';
            e.currentTarget.style.boxShadow = `0 4px 0 -1px ${item.edgeColor}40, 0 7px 0 -2px ${item.edgeColor}20, 0 10px 20px -6px ${item.shadowColor}, 0 2px 6px rgba(0,0,0,0.05)`;
          }} data-id="element-287">
                <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-xl bg-gradient-to-r from-white/0 via-white/80 to-white/0" data-id="element-288" />
                <div className={`w-9 h-9 ${item.ring} rounded-full flex items-center justify-center mx-auto mb-1.5`} data-id="element-289">
                  <span className="text-lg" data-id="element-290">{item.emoji}</span>
                </div>
                <span className={`block text-xl font-serif font-black text-${item.accent} leading-none mb-0.5`} data-id="element-291">
                  {item.number}
                </span>
                <span className="font-bold text-charcoal/80 text-[10px] tracking-wide leading-tight" data-id="element-292">
                  {item.label}
                </span>
              </div>)}
          </div>

          {/* 4th bonus card — smaller, centered */}
          <div className="flex justify-center mt-4" style={{
          perspective: '800px'
        }} data-id="element-293">
            <div className={`relative bg-white ${bonusCard.border} border rounded-xl px-4 py-3 text-center transition-all duration-300 overflow-visible cursor-pointer`} style={{
            transformStyle: 'preserve-3d',
            transform: 'rotateX(4deg) rotateY(0deg)',
            boxShadow: `0 4px 0 -1px ${bonusCard.edgeColor}40, 0 8px 0 -2px ${bonusCard.edgeColor}20, 0 10px 20px -6px ${bonusCard.shadowColor}, 0 2px 6px rgba(0,0,0,0.05)`,
            maxWidth: '140px'
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg) translateY(-3px)';
            e.currentTarget.style.boxShadow = `0 8px 0 -1px ${bonusCard.edgeColor}40, 0 12px 0 -2px ${bonusCard.edgeColor}20, 0 18px 30px -6px ${bonusCard.shadowColor}, 0 3px 10px rgba(0,0,0,0.07)`;
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = 'rotateX(4deg) rotateY(0deg)';
            e.currentTarget.style.boxShadow = `0 4px 0 -1px ${bonusCard.edgeColor}40, 0 8px 0 -2px ${bonusCard.edgeColor}20, 0 10px 20px -6px ${bonusCard.shadowColor}, 0 2px 6px rgba(0,0,0,0.05)`;
          }} data-id="element-294">
              <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-xl bg-gradient-to-r from-white/0 via-white/80 to-white/0" data-id="element-295" />
              <div className={`w-9 h-9 ${bonusCard.ring} rounded-full flex items-center justify-center mx-auto mb-1.5`} data-id="element-296">
                <span className="text-lg" data-id="element-297">{bonusCard.emoji}</span>
              </div>
              <span className={`block text-lg font-serif font-black text-${bonusCard.accent} leading-none mb-0.5`} data-id="element-298">
                {bonusCard.number}
              </span>
              <span className="font-bold text-charcoal/80 text-[10px] tracking-wide" data-id="element-299">
                {bonusCard.label}
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      <SectionDivider position="bottom" fillColor="fill-rose/20" data-id="element-300" />
    </section>;
}